import {ProofType} from '../constants/enumerations';
import {remote} from '../constants/environment';
import {IClient, IPaymentProof, ITaxProof} from '../interfaces/dtos';
import {IProofPaymentRequest} from '../interfaces/dtos/proof-payment-request.interface';
import {IStandardServiceResponse} from '../interfaces/services/standard-service.interface';
import {defaultFetchDelay} from '../constants/environment/defaults';
import {customFetch as fetch, remoteCall} from '../utils/services';
import {defaultHeaders} from './headers';

type paymentProof = {
  proof: IPaymentProof | ITaxProof;
  client?: IClient;
  type?: ProofType;
};

const buildUri = (type?: ProofType | undefined, client?: IClient) => {
  const pathClient = client ? `/${client.id}` : '';
  const remoteURI = remote.getSendProofUri(type, client ? true : false);
  return `${remoteURI}${pathClient}`;
};

const handleResponse = async (response: Response) => {
  if (response.status === 200) {
    const data = await response.json();
    const success = data.success && data.payload;
    const {payload, error} = data;
    return {
      success,
      payload,
      error,
    };
  }
  return null;
};

const sendPaymentProof = async (
  {proof, client}: paymentProof,
  token: string,
  hasTimeOut = true,
): Promise<IStandardServiceResponse> => {
  const controller = new AbortController();
  const signal = hasTimeOut ? controller.signal : null;
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, defaultFetchDelay);

  const customHandleResponse = async (response: Response) => {
    clearTimeout(timeoutId);
    return handleResponse(response);
  };

  const remoteURI = buildUri(ProofType.payment, client);

  return (await remoteCall(
    async () => {
      return await fetch(remoteURI, {
        method: 'POST',
        headers: {
          ...defaultHeaders,
          'token-session': token,
        },
        body: JSON.stringify({
          payload: proof,
        }),
        signal,
      });
    },
    customHandleResponse,
    remoteURI,
  )) as IStandardServiceResponse;
};

const sendTaxProof = async (
  {proof, type, client}: paymentProof,
  token: string,
  hasTimeOut = true,
): Promise<IStandardServiceResponse> => {
  const controller = new AbortController();
  const signal = hasTimeOut ? controller.signal : null;
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, defaultFetchDelay);

  const customHandleResponse = async (response: Response) => {
    clearTimeout(timeoutId);
    return handleResponse(response);
  };

  const remoteURI = buildUri(type, client);
  return (await remoteCall(
    async () => {
      return await fetch(remoteURI, {
        method: 'POST',
        headers: {
          ...defaultHeaders,
          'token-session': token,
        },
        body: JSON.stringify({
          payload: proof,
        }),
        signal,
      });
    },
    customHandleResponse,
    remoteURI,
  )) as IStandardServiceResponse;
};

export const postPaymentProof = async (
  paymentProof: IProofPaymentRequest,
  token: string,
  hasTimeOut = true,
): Promise<IStandardServiceResponse> => {
  const {proof, client, type} = paymentProof;
  if (type == null || type === ProofType.payment) {
    return sendPaymentProof({proof, client}, token, hasTimeOut);
  }
  return sendTaxProof({proof, type, client}, token, hasTimeOut);
};
